import React, { useState, useEffect, useMemo, useCallback } from 'react';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import style from 'assets/jss/material-dashboard-pro-react/components/fbbTableStyle';
import { AllInclusiveOutlined } from '@material-ui/icons';
import { TableCell, TableRow, makeStyles } from '@material-ui/core';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import { CustomAttribute, extractAttributeValue } from 'utils/ecommerce/services/extract';
import {
	PlanDetailAttributes,
	BoosterPlanDetailAttributes,
	addOnServiceAttribute,
	boosterTabType,
	fbbPlansTypes,
	planDetailTableColumnList,
	fbbPlanUpperLimitSpeed,
	fbbProTabType,
	fbbTabType,
	Offer_PRICE,
	Display_PRICE,
	commitmentTimesFiberPro,
} from './constants';
import { dynamicTranslate } from 'components/Translation';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { fbbPlanSelector } from 'redux/modules/fbbPlans/selectors';
import { useSelector } from 'react-redux';
import { dispatch } from 'redux/store';
import { setAllFbbPlans, setBoosterFbbPlans } from 'redux/modules/fbbPlans/actions';
import CustomTableComponent from 'views/AddNewServices/AddSingleService/tabs/common/CustomTableComponent';
import SimpleTabs from 'components/SimpleTabs/SimpleTabs';
import { BodyM, BodyS, BodySBold, HeadingMBold } from 'components/Typography/Typography';
import { TextWithIcon } from 'components/TextWithIcon/TextWithIcon';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { copperPlan, isBtlOfferOn, isFiberProOn } from 'utils/killswitch';
import { AccountsService } from 'services/accounts/accounts';
import { ChangePlanDetails } from 'views/Numbers/Details/NumberDetails/subcomponents/ChangePlanDialog';
import { formatPrice } from 'utils/price';
import { getCommonLabels, getFiberProLabels } from 'redux/modules/dynamicLabels/selectors';
import { SubscriberDetails } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import { numberTypeCopper } from 'views/Numbers/constants';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

const useStyle = makeStyles(style);

interface IFBBTableProps {
	selectedTab: types.services.fixed.IPlan | null;
	setSelectedTab: (value: types.services.fixed.IPlan | null) => void;
	subscriberNo?: string;
	currentPlanId?: string;
	currentCommitmentId?: string;
	selectedBoosterPlan?: string | null;
	setSelectedBoosterPlan?: (value: string | null) => void;
	changePlansDetails: ChangePlanDetails;
	setChangePlanDetails?: (value: ChangePlanDetails | null) => void;
	planTypeDetails?: SubscriberDetails;
	tarrifPlanId?: string;
	loadingPlans?: (value: boolean) => void;
}

const FBBTable = (props: IFBBTableProps) => {
	const allFbbPlans = useSelector(fbbPlanSelector);
	const remoteConfig = useSelector(remoteConfigSelector);
	const fiberProLabels = useSelector(getFiberProLabels);
	const commonLabels = useSelector(getCommonLabels);
	const isFiberPro = isFiberProOn(remoteConfig); // kill switch to hide fbb pro plan
	const isBtlOffer = isBtlOfferOn(remoteConfig); // kill switch to hide booster tab plan
	const {
		selectedTab,
		setSelectedTab,
		currentPlanId,
		currentCommitmentId,
		changePlansDetails,
		selectedBoosterPlan,
		setChangePlanDetails = () => {},
		setSelectedBoosterPlan = () => {},
		loadingPlans = () => {},
	} = props;
	const [loading, setLoading] = useState<boolean>(true);
	const [catalog, setCatalog] = useState<types.ecommerce.CatalogEntryView[]>([]);
	const [selectedFbbType, setSelectedFbbType] = useState<string>(
		isFiberPro ? fbbPlansTypes.fbbPro : fbbPlansTypes.fbb
	); // isFiberPro is kill switch for fiber pro
	const [commitment, setCommitment] = useState<number>(12);
	const [boosterPlansAvailable, setBoosterPlansAvailable] = useState<boolean>(false);
	const [planTabTyps, setPlanTabTypes] = useState<string[]>([fbbProTabType]);
	const [activeTab, setActiveTab] = useState<number>(0);
	const classes = useStyle();
	const fbbPlans = useSelector(fbbPlanSelector);
	const isCopperPlan = copperPlan(remoteConfig); // kill switch copper plan changes

	useEffect(() => {
		if (
			selectedFbbType === fbbPlansTypes.booster &&
			allFbbPlans.plans[selectedFbbType][commitment]?.planList.length === 1
		) {
			setChangePlanDetails({
				...changePlansDetails,
				newPlan: {
					name: allFbbPlans.plans[selectedFbbType][commitment]?.planList[0].plan,
					isPlanFiberPro:
						extractAttributeValue(
							allFbbPlans.plans[selectedFbbType][commitment]?.planList[0],
							CustomAttribute.FBB_PRO
						) === 'TRUE',
				},
			});
		}
	}, [selectedFbbType, commitment, allFbbPlans.plans[selectedFbbType][commitment]?.planList]);

	useEffect(() => {
		setLoading(true);
		// fetch fbb and fbb pro plans
		ECommerceService.getGroupContent(idConfig.FBB.FBB_FBB_PRO_PLANS[env]).subscribe(({ response }: any) => {
			setLoading(false);
			setCatalog(response.catalogEntryView);
		});
	}, []);
	// Making the columns of Plan Detail table
	const planDetailTableColumn = planDetailTableColumnList.map((column: string) => {
		return (
			<TableCell>
				<BodySBold customClassName={classes.colorWhite}>{column ? fiberProLabels[column] : ''}</BodySBold>
			</TableCell>
		);
	});
	// Extracting the current plan of the user
	const currentPlan: types.ecommerce.CatalogEntryView | undefined = useMemo(() => {
		return catalog.find((singleCatalogEntryView) => {
			return singleCatalogEntryView.partNumber === currentCommitmentId;
		});
	}, [catalog, currentCommitmentId]);

	// which tab will be visible we will check here
	useEffect(() => {
		if (!isFiberPro) {
			// if fiber pro kill switch is off then directly show fiber yab
			setPlanTabTypes([fbbTabType]);
			setSelectedFbbType(fbbPlansTypes.fbb);
		} else {
			// for adding new service it will always show fiber pro tab
			// checking the user is copper user
			if (currentPlan || props?.planTypeDetails?.subServiceType === numberTypeCopper) {
				if (boosterPlansAvailable) {
					// if booster plans avalialble show both fiber pro and booster tab
					setPlanTabTypes([fbbProTabType, boosterTabType]);
				} else {
					// else only show fiber pro tab
					setPlanTabTypes([fbbProTabType]);
				}
			} else {
				setPlanTabTypes([fbbProTabType]);
				setSelectedFbbType(fbbPlansTypes.fbbPro);
			}
		}
	}, [currentPlan, boosterPlansAvailable]);
	// set loading plans callback for previous screen
	useEffect(() => {
		loadingPlans(loading);
	}, [loading]);

	// Making the rows of Plan Detail table
	const getTableData = () => {
		if (selectedFbbType === fbbPlansTypes.booster) {
			if (allFbbPlans.plans[selectedFbbType][commitment]?.planList.length === 1) {
				setSelectedBoosterPlan(allFbbPlans.plans[selectedFbbType][commitment]?.planList[0].productId);
			}
			return allFbbPlans.plans[selectedFbbType][commitment]?.planList.map(
				(plan: types.ecommerce.BoosterPlans) => {
					const isChecked = plan?.productId === selectedBoosterPlan;

					return (
						<TableRow
							key={plan.productId}
							onClick={() => {
								setSelectedBoosterPlan(plan.productId);
								setChangePlanDetails({
									...changePlansDetails,
									newPlan: {
										name: plan.plan,
										isPlanFiberPro: false,
									},
								});
							}}
							aria-label={`Booster Plan ${plan.downloadSpeed}`}
							className={classes.selectedRow}
						>
							{allFbbPlans.plans[selectedFbbType][commitment]?.planList.length === 1 ? (
								<TableCell align="right"></TableCell>
							) : (
								<TableCell align="right">
									<CustomRadio
										checked={isChecked}
										onChange={() => {
											setSelectedBoosterPlan(plan.productId);
										}}
										radioProps={{ 'aria-label': 'Plans' }}
										formControlProps={{ role: 'radiogroup', 'aria-label': `Plan ${plan.plan}` }}
									/>
								</TableCell>
							)}

							<TableCell>
								<BodyS customClassName={classes.marginTop}>{plan.plan}</BodyS>
							</TableCell>
							{Object.keys(BoosterPlanDetailAttributes).map((attribute, index) => {
								return (
									<TableCell key={index}>
										<BodyS customClassName={classes.marginTop}>
											{getBoosterTableCell(plan, attribute)}
										</BodyS>
									</TableCell>
								);
							})}
							<TableCell>
								<BodyS customClassName={classes.marginTop}>
									{dynamicTranslate(commonLabels.price, formatPrice(plan.price))}
								</BodyS>
							</TableCell>
						</TableRow>
					);
				}
			);
		}
		return allFbbPlans.plans[selectedFbbType][commitment]?.planList.map((plan: any) => {
			const isChecked = plan?.uniqueID === selectedTab;
			const prices = plan?.price || [];
			const price = {
				offer: prices.find((singlePrice: any) => singlePrice.usage === Offer_PRICE),
				display: prices.find((singlePrice: any) => singlePrice.usage === Display_PRICE),
			};
			let isDisabled = false;
			if (currentPlan) {
				const currentPlanTypeFbbPro = extractAttributeValue(currentPlan, CustomAttribute.FBB_PRO);
				let planHierarchyIndex = extractAttributeValue(plan, CustomAttribute.HIERARCHY_INDEX);
				let currentPlanHierarchyIndex = extractAttributeValue(currentPlan, CustomAttribute.HIERARCHY_INDEX);
				if (
					currentPlanTypeFbbPro === 'TRUE' &&
					(planHierarchyIndex <= currentPlanHierarchyIndex || commitment < currentPlanContractDuration)
				) {
					isDisabled = true;
				} else {
					isDisabled = planHierarchyIndex <= currentPlanHierarchyIndex;
				}
			}
			return (
				<TableRow
					key={plan.uniqueID}
					onClick={() => {
						setSelectedTab(plan);
						setChangePlanDetails({
							...changePlansDetails,
							newPlan: {
								name: plan.shortDescription,
								isPlanFiberPro: extractAttributeValue(plan, CustomAttribute.FBB_PRO) === 'TRUE',
							},
						});
					}}
					className={isDisabled ? classes.disabled : isChecked ? classes.selectedRow : classes.unSelectedRow}
				>
					<TableCell align="right">
						<CustomRadio
							checked={isChecked}
							onChange={() => {}}
							disabled={isDisabled}
							radioProps={{ 'aria-label': 'Plans' }}
							formControlProps={{ role: 'radiogroup', 'aria-label': `Plan ${plan.name}` }}
						/>
					</TableCell>
					<TableCell>
						<BodyS>{plan.name}</BodyS>
					</TableCell>
					{Object.keys(PlanDetailAttributes).map((attribute, index) => {
						return (
							<TableCell key={index}>
								<BodyS>{getTableCell(plan, attribute)}</BodyS>
							</TableCell>
						);
					})}
					<TableCell>
						<BodyS>{dynamicTranslate(commonLabels.price, formatPrice(price.display.value))}</BodyS>
					</TableCell>
				</TableRow>
			);
		});
	};

	// extract and Format Cell Data based on the attribute type
	function getTableCell(plan: any, attribute: string) {
		switch (attribute) {
			case PlanDetailAttributes.HBB_UP_SPEED:
			case PlanDetailAttributes.HBB_DOWN_SPEED:
				return extractAttributeValue(plan, attribute);
			case PlanDetailAttributes.INCLUDED_MINUTES:
				return extractAttributeValue(plan, attribute) === 'unlimited' ? (
					<AllInclusiveOutlined />
				) : (
					extractAttributeValue(plan, attribute)
				);
			default:
				return '';
		}
	}
	// get booster table cells
	function getBoosterTableCell(plan: any, attribute: string) {
		switch (attribute) {
			case BoosterPlanDetailAttributes.uploadSpeed:
			case BoosterPlanDetailAttributes.downloadSpeed:
			case BoosterPlanDetailAttributes.callsToMobile:
				return plan[attribute];
			default:
				return '';
		}
	}
	const currentPlanContractDuration = useMemo(() => {
		if (currentPlan) {
			return parseInt(extractAttributeValue(currentPlan, CustomAttribute.CONTRACT_DURATION));
		}
		return 0;
	}, [currentPlan]);
	const currentPlanFbbProType = useMemo(() => {
		if (currentPlan) {
			return extractAttributeValue(currentPlan, CustomAttribute.FBB_PRO);
		}
		return 'false';
	}, [currentPlan]);

	const handleSetActiveTab = (activaTabIndex: React.SetStateAction<number>) => {
		// set tabs and commitment
		setActiveTab(activaTabIndex);
	};
	// Storing the fbb and fbb pro plans in redux
	useEffect(() => {
		//if the user type is copper user and copperPlan kill switch is on
		const isCopperUser = props?.planTypeDetails?.subServiceType === numberTypeCopper && isCopperPlan;

		if (catalog?.length)
			dispatch(
				setAllFbbPlans({
					availableCatalogEntryViews: catalog,
				})
			);
		if (currentPlan || isCopperUser) {
			// setCommitment(currentPlanContractDuration as CommitmentType);
			// setSelectedTab(currentPlan);
			let currentPlanSpeed;
			if (currentPlan) {
				const currentSpeed = extractAttributeValue(currentPlan, CustomAttribute.HBB_DOWN_SPEED);
				let matches = currentSpeed.match(/(\d+)/);

				// Display output if number extracted
				if (matches) {
					currentPlanSpeed = matches[0];
				}
				setChangePlanDetails({
					...changePlansDetails,
					oldPlan: {
						name: currentPlan.shortDescription,
						isPlanFiberPro: currentPlanFbbProType === 'TRUE',
					},
				});
			}

			if (currentPlanFbbProType === 'TRUE') {
				setCommitment(currentPlanContractDuration);
			}
			// check if current plan type is less speed to show booster plan and booster kill switch is on
			if (
				(currentPlanSpeed < fbbPlanUpperLimitSpeed ||
					props?.planTypeDetails?.subServiceType === numberTypeCopper) && // checking if user type is copper
				props.subscriberNo &&
				isBtlOffer
			) {
				// fetch booster plans
				setLoading(true);
				// need to send tarrif id for copper user as current plan is not in crm
				AccountsService.getBoosterPlans(props.subscriberNo, isCopperUser, props.tarrifPlanId)
					.pipe(
						catchError((error) => {
							setLoading(false);
							return throwError(error);
						})
					)
					.subscribe((res) => {
						if (res && res.response && res.response.data) {
							setLoading(false);
							if (res.response.data.length > 0) {
								setBoosterPlansAvailable(true);
								dispatch(
									setBoosterFbbPlans({
										plans: res.response.data,
									})
								);
							}
						}
					});
			}
		}
	}, [catalog]);
	useEffect(() => {
		setSelectedFbbType(planTabTyps[activeTab]);
		// on tab change set default selected year which have data
		commitmentTimesFiberPro.every((commitment: number) => {
			if (allFbbPlans.plans[planTabTyps[activeTab]][commitment]?.planList.length > 0) {
				setCommitment(commitment);
				return false;
			} else {
				return true;
			}
		});
	}, [activeTab]);

	// genrating common addons if addon length is equal to total plan length
	const getCommonAddons = (catalog: types.ecommerce.CatalogEntryView[]) => {
		const commonAddons: string[] = [];
		const length = catalog.length;
		const parsedAddons = {};
		catalog.forEach((plan, i) => {
			const filteredAttribute = plan.attributes.filter(
				(attribute) => attribute.identifier === addOnServiceAttribute
			);
			const stringifiedAttributes = filteredAttribute[0]?.values[0]?.value || '{}';
			const parsedAttributes = stringifiedAttributes.split(',');
			parsedAttributes.map((addon: string) => {
				if (parsedAddons[addon]) {
					parsedAddons[addon] += 1;
				} else {
					parsedAddons[addon] = 1;
				}
			});
		});
		Object.keys(parsedAddons).forEach((key) => {
			if (parsedAddons[key] === length) {
				commonAddons.push(key);
			}
		});
		return commonAddons;
	};
	const getTabName = (tabName: string) => {
		if (tabName === fbbPlansTypes.fbbPro) return fiberProLabels.fiberPro;
		else if (tabName === fbbPlansTypes.fbb) return fiberProLabels.fbb;

		return fiberProLabels.booster;
	};
	const showCommitmentOption = (number: number) => {
		return allFbbPlans.plans[selectedFbbType][number]?.planList.length > 0;
	};
	return (
		<ConditionalRender
			show={loading}
			onTrue={() => <CustomCircularProgress loading text="" transparent />}
			onFalse={() => (
				<SimpleTabs
					tabs={planTabTyps.map((fbbTab, index) => ({
						name: getTabName(fbbTab),
						label: fbbTab === fbbPlansTypes.fbbPro ? fiberProLabels.newLabel : '',
						content: (
							<div tabIndex={1}>
								<div className={classes.commitmentWrapper}>
									<HeadingMBold color="primary-light">
										{fiberProLabels.selectPlanHeading}
									</HeadingMBold>
									<div className={classes.commitmentSelection}>
										<ConditionalRender
											show={showCommitmentOption(12)}
											onTrue={() => (
												<div className={classes.radioContainer}>
													<CustomRadio
														label={
															<BodyM disableMargin>{fiberProLabels.oneYearCont}</BodyM>
														}
														checked={commitment === 12}
														onChange={() => setCommitment(12)}
													/>
												</div>
											)}
										/>
										<ConditionalRender
											show={showCommitmentOption(24)}
											onTrue={() => (
												<div className={classes.radioContainer}>
													<CustomRadio
														label={
															<BodyM disableMargin>{fiberProLabels.twoYearCont}</BodyM>
														}
														checked={commitment === 24}
														onChange={() => setCommitment(24)}
													/>
												</div>
											)}
										/>
										<ConditionalRender
											show={showCommitmentOption(36)}
											onTrue={() => (
												<div className={classes.radioContainer}>
													<CustomRadio
														label={
															<BodyM disableMargin>{fiberProLabels.threeYearCont}</BodyM>
														}
														checked={commitment === 36}
														onChange={() => setCommitment(36)}
													/>
												</div>
											)}
										/>
									</div>
								</div>
								<CustomTableComponent
									tableColumns={planDetailTableColumn}
									tableRows={getTableData()}
									customClassName={
										selectedFbbType === fbbPlansTypes.booster &&
										allFbbPlans.plans[selectedFbbType][commitment]?.planList.length === 1
											? classes.pullLeft
											: {}
									}
								/>
								{/* showing common addons here */}
								{selectedFbbType === fbbPlansTypes.fbbPro && (
									<>
										<div>
											<BodyM>{fiberProLabels.allPacHeading}</BodyM>
											<TextWithIcon
												displayInline
												items={getCommonAddons(
													allFbbPlans.plans[selectedFbbType][commitment]?.planList
												)}
											/>
										</div>
									</>
								)}
							</div>
						),
					}))}
					activeTab={activeTab}
					setActiveTab={handleSetActiveTab}
				/>
			)}
		/>
	);
};

export default FBBTable;
